import { MatTable, MatTableModule } from '@angular/material/table';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  afterNextRender,
} from '@angular/core';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';

import {
  formatFilesize,
  formatISODuration,
  formatLicenseExpiresIn,
  formatLicenseValidity,
  formatSecondsProperty,
  formatUnlimited,
  getExpiryStatus,
  getHintForReleaseStatus,
  getIconForLicenseType,
  getIconForOs,
  getIconForReleaseStatus,
  getLicenseActivations,
  getLicenseDeactivations,
  getLicenseStatus,
  getNameForId,
  getObjectProperty,
  getResourceDisplayName,
  projectEnv,
  sortByKey,
  getSubscriptionStartTriggerLabel,
  formatAllowedActivations,
} from 'utils';
import {
  TABLE_SORT_ASC_OPERATOR,
  TABLE_SORT_DESC_OPERATOR,
  TableSortQueryParam,
} from 'utils';
import type { ResourceTableService } from '../../../_services/resource-table.service';
import { ScreenSizeService } from '../../../_services/screen-size.service';
import {
  CachedResourceName,
  DataCacheService,
} from '../../../_services/data-cache.service';
import { CtxTablePaginatorComponent } from '../paginator/paginator.component';
import { CtxTagComponent } from '../../tag/tag.component';
import { ViewInModalButtonComponent } from '../view-in-modal-button/view-in-modal-button.component';
import { RouterLink } from '@angular/router';
import { CtxResourceActionsComponent } from '../../resource-actions/resource-actions.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  AsyncPipe,
  DatePipe,
  DecimalPipe,
  NgClass,
  NgFor,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
} from '@angular/common';
import { HlmIconComponent } from '../../icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'table-content',
  templateUrl: './content.component.html',
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    MatTooltipModule,
    NgSwitch,
    NgSwitchCase,
    MatProgressSpinnerModule,
    HlmIconComponent,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    CtxResourceActionsComponent,
    NgFor,
    RouterLink,
    ViewInModalButtonComponent,
    CtxTagComponent,
    NgSwitchDefault,
    CtxTablePaginatorComponent,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
  ],
})
export class CtxTableContentComponent implements OnInit {
  /**
   * ResourceTableService instance
   */
  @Input() resourceTableService: ResourceTableService;

  /**
   * Ref to MatTable element
   */
  @ViewChild(MatTable) matTable: MatTable<any>;

  /**
   * Ref to section that wraps the MatTable
   */
  @ViewChild('tableWrapper') tableWrapper: ElementRef<any>;

  @ViewChild(MatSort) matSort: MatSort;

  tags = this.dataCacheService.getCachedValues('tag');

  // Icons

  /**
   * Width of the table overlay in pixels as a string.
   */
  tableWidth: string;
  projectName = projectEnv.get('projectName');
  @HostListener('window:resize', ['$event']) handleResize() {
    this.setTableOverlayWidth();
  }

  formatSecondsProperty = formatSecondsProperty;

  /** Set the static function as a property to use it in the template */
  getIconForLicense = getIconForLicenseType;
  getIconForReleaseStatus = getIconForReleaseStatus;
  getHintForReleaseStatus = getHintForReleaseStatus;
  formatFilesize = formatFilesize;

  getResourceDisplayName = getResourceDisplayName;

  getObjectProperty = getObjectProperty;

  getIconForOs = getIconForOs;

  getLicenseActivations = getLicenseActivations;
  getLicenseDeactivations = getLicenseDeactivations;
  getLicenseStatus = getLicenseStatus;

  formatUnlimited = formatUnlimited;
  constructor(
    public screenSize: ScreenSizeService,
    private dataCacheService: DataCacheService
  ) {
    afterNextRender(() => {
      this.setTableOverlayWidth();
    });
  }

  ngOnInit() {
    this.resourceTableService.getTablePreferences();
    this.resourceTableService.tableList();
  }

  /**
   * Sets the table overlay width. This is required because the position is static on the overlay.
   */
  setTableOverlayWidth() {
    if (this.tableWrapper && this.tableWrapper.nativeElement.offsetWidth) {
      // Only available after the ngAfterViewInit lifecyse hook has been called.
      this.tableWidth = this.tableWrapper.nativeElement.offsetWidth
        .toString()
        .concat('px');
    } else {
      this.tableWidth = '0px';
    }
  }

  /**
   * This property copies the columnsIdsToDisplay property from ResourceTableService
   * and adds the checkbox and action column as required. The columnIdsToDisplay in
   * ResourceTableService does not contain these as it is important to keep the data in that array
   * with properties in the Cryptlex Web API. This will allow cleaner interop with Segments.
   */
  get columnsToDisplay() {
    // Copy array present in resourceTableService
    const columnsIdsToDisplayLocal = [
      ...this.resourceTableService.columnIdsToDisplay,
    ];

    // If resourceTableService has menuActions, THEN add actions column
    if (
      this.resourceTableService.actions.menuActions &&
      this.resourceTableService.actions.menuActions.length !== 0
    ) {
      columnsIdsToDisplayLocal.push('actions');
    }

    // IF resourceTableService allows selection, THEN add checkbox column
    if (this.resourceTableService.actions.selection) {
      columnsIdsToDisplayLocal.unshift('checkbox');
    }

    return columnsIdsToDisplayLocal;
  }

  /**
   * Sets the correct sorting order in the ResourceTableService.
   * @param sort Sort object returned when matSortChange is triggered
   */
  async handleSortChange(sort: Sort) {
    this.resourceTableService.tableSort = this.generateSortApiParam(sort);
    await this.resourceTableService.tableList();
    await this.resourceTableService.saveTablePreferences();
  }

  /**
   * @returns A TableSortQueryParam object for the Sort object parameter.
   */
  private generateSortApiParam(sort: Sort): TableSortQueryParam {
    if (sort.direction === 'asc') {
      return {
        sort: `${TABLE_SORT_ASC_OPERATOR}${sort.active}`,
      };
    } else {
      return {
        sort: `${TABLE_SORT_DESC_OPERATOR}${sort.active}`,
      };
    }
  }

  /**
   * Returns true if the row data is of a license, or similar resource.
   */
  isRowLicenseBased(row: any): boolean {
    return 'type' in row && 'allowedActivations' in row;
  }

  /**
   * @returns Name for resource id, if present in DataCache
   */
  getName(resourceName: CachedResourceName, id: string): string {
    return getNameForId(
      this.dataCacheService.getCachedValues(resourceName),
      id
    );
  }

  getEntitlementSetName(row: any): string {
    if (projectEnv.get('projectName') === 'admin-portal') {
      return this.getName(
        'entitlement-set',
        getObjectProperty(row, 'entitlementSetId')
      );
    } else {
      return getObjectProperty(row, 'entitlementSet.name');
    }
  }
  formatAllowedActivations = formatAllowedActivations;
  meterAttributeColumns =
    this.projectName === 'admin-portal'
      ? [
          'name',
          'allowedUses',
          'totalUses',
          'grossUses',
          'viewPermissions',
          'floating',
        ]
      : ['name', 'allowedUses', 'totalUses', 'grossUses', 'floating'];
  /**
   * @returns Description of tag from cached values for the passed name param.
   */
  getTagDescription(name: string) {
    if (this.tags && Array.isArray(this.tags)) {
      const selectedTag = this.tags.find((tag) => {
        return name === tag?.name;
      });
      return selectedTag?.description;
    } else {
      return '';
    }
  }
  /**
   * Finds the name of the organization or user from the row data.
   * @param row
   * @returns name
   */
  getLicenseCustomer(row: any) {
    if ('organization' in row && row.organization) {
      return getObjectProperty(row, 'organization.name');
    } else if ('user' in row) {
      return getObjectProperty(row, 'user.name');
    } else {
      return null;
    }
  }
  formatLicenseValidity = formatLicenseValidity;
  formateLiceseEXpiresIn = formatLicenseExpiresIn;
  getSubscriptionStartTriggerLabel = getSubscriptionStartTriggerLabel;
  /**
   * @param productIds
   * @returns Display name of the products linked to the resource
   */
  getLinkedProducts(productIds: string[]) {
    if (!productIds) {
      return '';
    }
    return productIds
      .map((productId) => {
        return this.getName('product', productId);
      })
      .join(', ');
  }

  /**
   * @param usergroupIds
   * @returns Display name of the user groups linked to the resource
   */
  getLinkedUserGroups(userGroups: any[]) {
    if (!userGroups) {
      return '';
    }
    return userGroups
      .map((userGroup) => {
        return userGroup.name;
      })
      .join(', ');
  }
  /** True if column is not sortable */
  isColumnSortable(property: string): boolean {
    const NON_SORTABLE_COLUMN_PROPS = [
      'licensePolicy.id',
      'trialPolicy.id',
      'metadata',
      'meterAttributes',
      'requiredMetadataKeys',
      'requiredMeterAttributes',
      'changes',
      'files',
      'totalTrialActivations',
      'featureFlags',
      'automatedEmails',
      'claims',
      'location.countryName',
      'status',
      'validity',
      'scopes',
      'allowedCountries',
      'disallowedCountries',
      'allowedIpAddresses',
      'disallowedIpAddresses',
      'allowedIpRanges',
      'notes',
      'events',
      'eventData',
      'body',
      'customer',
      'expiresIn',
      'createdBy',
      'entitlements',
      'products',
      'activations',
    ];
    return !NON_SORTABLE_COLUMN_PROPS.includes(property);
  }
  formatISODuration = formatISODuration;
  getExpiryStatus = getExpiryStatus;
  sortByKey = sortByKey;
  /**
   *
   * @param notes
   * @returns  Return the formatted notes based on length
   */
  formatLicenseNotes(notes: string): string | null {
    if (!notes) {
      return null;
    }
    return notes.length > 30 ? `${notes.slice(0, 30)}...` : notes;
  }

  formatLinkedProducts(productIds: string[]): {
    products: string;

    viewButton: boolean;
  } {
    if (!productIds) {
      return {
        products: '',
        viewButton: false,
      };
    }
    const productsNames = productIds
      .map((productId) => {
        return this.getName('product', productId);
      })
      .join(', ');
    return {
      products:
        productsNames.length > 50
          ? `${productsNames.slice(0, 50)}...`
          : productsNames,
      viewButton: productsNames.length > 50,
    };
  }
}
